<div class="px-10 pt-6 pb-10">
  <div class="flex items-center justify-between mb-6">
    <div class="flex flex-col gap-1">
      <h1 class="text-2xl font-bold text-primary-dark">
        Configurações do App
      </h1>
    </div>

    <button
      type="button"
      class="px-5 py-2.5 rounded-lg font-medium text-white bg-primary disabled:opacity-60 disabled:cursor-not-allowed"
      [disabled]="!hasPendingChanges() || isSaving || isLoading || exitRadiusError"
      (click)="saveChanges()"
    >
      {{ isSaving ? "Salvando..." : "Salvar Alterações" }}
    </button>
  </div>

  <div *ngIf="isLoading" class="py-16 flex justify-center">
    <p class="text-primary-gray">Carregando configurações...</p>
  </div>

  <form *ngIf="!isLoading" [formGroup]="form" class="flex flex-col gap-6">
    <!-- Seção: Justificativas de Check-in / Check-out -->
    <app-section-card
      icon="/assets/icons/section/user-clock.svg"
      title="Justificativas de Check-in / Check-out"
      description="Configure quando os consultores devem fornecer justificativa para check-in e check-out"
    >
      <div class="flex flex-col gap-4">
        <div class="flex items-center justify-between p-3 bg-gray-50 rounded">
          <div class="flex items-start gap-3 min-w-0">
            <div class="w-9 h-9 flex items-center justify-center flex-shrink-0">
              <i class="fa-solid fa-id-badge text-[20px] text-gray-700"></i>
            </div>
            <div class="min-w-0">
              <p class="text-sm font-semibold text-primary-dark">Check-in fora da base</p>
              <p class="text-xs text-primary-gray mt-1">Exige justificativa quando o consultor faz check-in em local diferente da base</p>
            </div>
          </div>
          <app-custom-toggle
            [checked]="requireCheckInOutOfBaseJustification"
            (checkedChange)="requireCheckInOutOfBaseJustification = $event"
          ></app-custom-toggle>
        </div>

        <div class="flex items-center justify-between p-3 bg-gray-50 rounded">
          <div class="flex items-start gap-3 min-w-0">
            <div class="w-9 h-9 flex items-center justify-center flex-shrink-0">
              <img src="/assets/icons/user-circle-question.svg" alt="" class="w-[18px] h-4" />
            </div>
            <div class="min-w-0">
              <p class="text-sm font-semibold text-primary-dark">Check-in em local diferente do check-out anterior (pernoite)</p>
              <p class="text-xs text-primary-gray mt-1">Exige justificativa quando o consultor faz check-in em local diferente da base configurada</p>
            </div>
          </div>
          <app-custom-toggle
            [checked]="requireOvernightLocationJustification"
            (checkedChange)="requireOvernightLocationJustification = $event"
          ></app-custom-toggle>
        </div>

        <div class="flex items-center justify-between p-3 bg-gray-50 rounded">
          <div class="flex items-start gap-3 min-w-0">
            <div class="w-9 h-9 flex items-center justify-center flex-shrink-0">
              <img src="/assets/icons/user-circle-exclamation.svg" alt="" class="w-[18px] h-4" />
            </div>
            <div class="min-w-0">
              <p class="text-sm font-semibold text-primary-dark">Check-out fora da base (pernoite ou somente volta)</p>
              <p class="text-xs text-primary-gray mt-1">Exige justificativa quando o consultor faz check-out em local diferente da base</p>
            </div>
          </div>
          <app-custom-toggle
            [checked]="requireCheckOutOutOfBaseJustification"
            (checkedChange)="requireCheckOutOutOfBaseJustification = $event"
          ></app-custom-toggle>
        </div>
      </div>
    </app-section-card>

    <!-- Seção: Regras de Geolocalização -->
    <app-section-card
      icon="/assets/icons/section/user-clock.svg"
      title="Regras de Geolocalização"
      description="Configure as distâncias para controle de check-in e check-out automático em lojas"
    >
      <div class="flex flex-wrap gap-4">
        <div class="flex-1 min-w-[280px]">
          <label class="input-label">Raio aceitável para check-in <span class="text-red-600">*</span></label>
          <div class="flex items-center gap-2">
            <input
              type="number"
              class="w-full border rounded-lg px-3 py-2.5 input-field-focus"
              [class.border-red-500]="form.controls.checkInRadiusMeters.invalid && form.controls.checkInRadiusMeters.touched"
              formControlName="checkInRadiusMeters"
              placeholder="Ex: 150"
              (keydown)="preventNonIntegerInput($event)"
            />
            <span class="text-sm text-primary-gray whitespace-nowrap">metros</span>
          </div>
          <p class="mt-1 text-xs text-primary-gray">Distância máxima permitida entre consultor e loja para realizar check-in</p>
          <p
            *ngIf="form.controls.checkInRadiusMeters.invalid && form.controls.checkInRadiusMeters.touched"
            class="mt-1 text-xs text-red-600"
          >
            O raio de check-in deve ser maior que zero.
          </p>
        </div>

        <div class="flex-1 min-w-[280px]">
          <label class="input-label">Distância mínima para check-out automático <span class="text-red-600">*</span></label>
          <div class="flex items-center gap-2">
            <input
              type="number"
              class="w-full border rounded-lg px-3 py-2.5 input-field-focus"
              [class.border-red-500]="(form.controls.exitRadiusMeters.invalid && form.controls.exitRadiusMeters.touched) || exitRadiusError"
              formControlName="exitRadiusMeters"
              placeholder="Ex: 250"
              (keydown)="preventNonIntegerInput($event)"
            />
            <span class="text-sm text-primary-gray whitespace-nowrap">metros</span>
          </div>
          <p class="mt-1 text-xs text-primary-gray">Distância a partir da loja para que o check-out seja realizado automaticamente</p>
          <p
            *ngIf="exitRadiusError"
            class="mt-1 text-xs text-red-600"
          >
            A distância de check-out automático deve ser maior que o raio de check-in.
          </p>
        </div>
      </div>
    </app-section-card>

    <!-- Seção: Notificações do Consultor -->
    <app-section-card
      icon="/assets/icons/section/bells.svg"
      title="Notificações do Consultor"
      description="Configure as notificações automáticas enviadas aos consultores"
    >
      <div class="flex flex-col">
        <div
          *ngFor="let rule of notificationRules; let isLast = last"
          class="flex items-center justify-between gap-4 p-3 mb-4 bg-gray-50 rounded"
          [class.border-b]="!isLast"
          [class.border-gray-100]="!isLast"
        >
          <div class="flex items-start gap-3 min-w-0">
            <div class="w-9 h-9 flex items-center justify-center flex-shrink-0">
              <i [class]="rule.icon + ' text-[20px] text-gray-700'"></i>
            </div>

            <div class="min-w-0">
              <p class="text-sm font-semibold text-primary-dark">
                {{ rule.name }}
              </p>
              <p class="text-xs text-primary-gray mt-1">
                {{ rule.description }}
              </p>
            </div>
          </div>

          <div class="flex items-center gap-3 flex-shrink-0">
            <button
              type="button"
              class="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 hover:bg-gray-200 transition-colors"
              (click)="openEditTemplate(rule)"
            >
              <i class="fa-solid fa-pen-to-square text-sm text-gray-700"></i>
              Editar texto
            </button>

            <app-custom-toggle
              [checked]="rule.isEnabled"
              (checkedChange)="onRuleToggle(rule.type, $event)"
            ></app-custom-toggle>
          </div>
        </div>
      </div>
    </app-section-card>
  </form>
</div>
